import { html, css } from 'lit-element';
import axios from 'axios';
import { TmnToolBase } from '@trumedianetworks/tmn-core-components/src/tmn-tool-base';
import '@trumedianetworks/tmn-core-components/src/tmn-icon';
import '@github/clipboard-copy-element';
class ActAsBtn extends TmnToolBase {
    constructor() {
        super();
        this.disabled = false;
        this.controlType = 'icon-button';
        this.useBackDrop = true;
        this.useCloseIcon = true;
        this.fullscreenModal = true;
        this.useClickAway = false;
        this.closeOnEscapePress = false;
        this.useIconButton = true;
    }
    static get styles() {
        return [
            super.styles,
            css`
                .modal-content {
                    padding: 8px;
                    width: 355px;
                    min-height: 250px;
                    text-wrap: pretty;
                }

                .confirmation-label {
                    overflow-wrap: break-word;
                    text-align: center;
                    white-space: normal;
                }
                .footer {
                    padding: 10px;
                }
                .error-message {
                    color: var(--app-error-color);
                    padding: 12px;
                    justify-content: center;
                }
                clipboard-copy {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    --app-success-color: #00695c;
                    display: flex;
                }
                clipboard-copy > * {
                    margin: 4px;
                    cursor: pointer;
                }
                input {
                    display: flex;
                    flex: 1;
                    background: var(--light-gray-100);
                    padding: 4px;
                    border: 1px solid var(--light-gray-200);
                }
                .explainer {
                    display: flex;
                }
                li {
                    margin-bottom: 6px;
                }
                ul {
                    margin-block-start: 0.5em;
                    margin-block-end: 0.5em;
                    padding-inline-start: 30px;
                }
                .warning {
                    font-style: italic;
                }
            `,
        ];
    }

    static get properties() {
        return {
            disabled: {
                type: Boolean,
            },
            eventData: {
                type: Object,
            },
            eUserId: {
                type: String,
            },
            actAsUrl: {
                type: String,
            },
            copied: {
                type: Boolean,
            },
            error: {
                type: String,
            },
            eSiteId: {
                type: String,
            },
            useIconButton: {
                type: Boolean,
            },
        };
    }

    updated(changedProps) {
        if (changedProps.has('eUserId') || changedProps.has('eSiteId')) {
            this.actAsUrl = '';
            this.copied = false;
            this.error = null;
        }
    }

    getConfirmButtonLabel() {
        return 'Act as user';
    }

    getToolTitle() {
        return 'Act as user';
    }

    getCancelButtonLabel() {
        // Overwrite in subclass if necessary
        return 'Cancel';
    }

    getWarning() {
        return html`<span class="warning"
            >This feature requires that there are no active logins to the destination TruMedia site
            in this browser. Please log out of any applicable accounts before continuing.</span
        >`;
    }

    renderButton() {
        return this.useIconButton ? this.renderIconButton() : this.renderStandardButton();
    }
    renderStandardButton() {
        return html`
            <tmn-button
                title="Act As User"
                @click=${this.handleGenerateActAs}
                .eventData=${this.eventData}>
                <tmn-icon
                    iconvariant="outlined"
                    slot="startIcon"
                    icon="supervised_user_circle"></tmn-icon>
                Act as</tmn-button
            >
        `;
    }
    renderIconButton() {
        return html`<tmn-icon-button
            title="Act As User"
            @click=${this.handleGenerateActAs}
            .eventData=${this.eventData}
            variant="text">
            <tmn-icon iconvariant="outlined" icon="supervised_user_circle"></tmn-icon>
        </tmn-icon-button>`;
    }

    renderToolTitle() {
        return html` <span class="modal-title" slot="title">${this.getToolTitle()}</span> `;
    }

    renderDetails() {
        const { eventData } = this;
        const displayName = eventData?.metaData?.rawData?.display_name;

        return html`<tmn-grid item>
                <div class="explainer">
                    Use the act as button to open a new window and login as ${displayName}.
                </div>
            </tmn-grid>
            <tmn-grid item>
                <ul>
                    <li>
                        If the user is able to login to multiple sites you can edit the url to one
                        of those sites before logging in.
                    </li>
                    <li>Alternatively, you may copy the url and paste it into another window.</li>
                </ul>
            </tmn-grid>
            <tmn-grid container item direction="row" wrap="nowrap" alignItems="center">
                <clipboard-copy
                    @clipboard-copy=${this.handleCopy}
                    value=${this.actAsUrl}
                    for="blob-path">
                    ${this.copied
                        ? html`<tmn-icon
                              color="success"
                              icon="done"
                              iconvariant="outlined"></tmn-icon>`
                        : html`<tmn-icon icon="copy" iconvariant="outlined"></tmn-icon>`}
                </clipboard-copy>
                <input
                    id="blob-path"
                    class="token-input"
                    value=${this.actAsUrl}
                    @blur=${this.handleActAsBlur} />
            </tmn-grid>
            <tmn-grid item>${this.getWarning()}</tmn-grid>

            ${this.error
                ? html`<tmn-grid><div class="error-message">Error: ${this.error}</div></tmn-grid>`
                : null}`;
    }

    renderToolContent() {
        return html`
            <tmn-grid class="modal-content" container direction="column" spacing="1">
                ${this.renderDetails()}
            </tmn-grid>
        `;
    }

    renderToolFooter() {
        const disableConfirm = !this.actAsUrl;
        return html`
            <tmn-grid class="footer" container direction="row" justify="center" spacing="1">
                <tmn-grid item>
                    <tmn-button
                        .disabled=${disableConfirm}
                        class="confirm-button"
                        variant="outlined"
                        color="primary"
                        @click=${this._handleConfirm}>
                        ${this.getConfirmButtonLabel()}
                    </tmn-button>
                </tmn-grid>
                <tmn-grid item>
                    <tmn-button
                        class="cancel-button"
                        variant="outlined"
                        @click=${this._toggleDialog}>
                        ${this.getCancelButtonLabel()}
                    </tmn-button>
                </tmn-grid>
            </tmn-grid>
        `;
    }

    _handleConfirm(e) {
        const { actAsUrl } = this;
        this._toggleDialog();
        if (actAsUrl) {
            window.open(actAsUrl, '_blank');
        }
    }

    handleActAsBlur(e) {
        const { target } = e;
        const { actAsUrl } = this;
        const newValue = target?.value;
        if (actAsUrl !== newValue) {
            this.copied = false;
        }
        this.actAsUrl = newValue;
    }

    handleCopy(e) {
        this.copied = true;
    }

    handleGenerateActAs() {
        const { actAsUrl } = this;
        this._toggleDialog();
        if (!actAsUrl) {
            this.fetchActAs();
        }
    }

    fetchActAs() {
        const { eSiteId, eventData } = this;
        const eUserId = eventData?.metaData?.id;
        const params = { eUserId, eSiteId };
        const headers = {
            Accept: 'application/json',
        };
        axios
            .request({
                url: '/v2/api/user-admin/act-as-user',
                method: 'POST',
                headers,
                withCredentials: true,
                data: params,
            })
            .then((data) => this.handleActAsResponse(data))
            .catch((err) => {
                this.error = 'Unexpected error getting act as url';
                console.error(err);
            });
    }

    handleActAsResponse({ statusText, data }) {
        // todo better missing messages
        if (statusText === 'OK' && data.success) {
            this.actAsUrl = data.url || 'Url Missing';
            return;
        }
        this.error = data.message;
    }
}

window.customElements.define('act-as-btn', ActAsBtn);
